import React, { useEffect, useRef, useState } from "react";
import { Fingerprint, Loader2, PhoneIncoming, CheckCircle2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";

export const OwnCallerIdCard = ({ initial, defaultNumber, onChange }) => {
  const [state, setState] = useState(initial || {});
  const [number, setNumber] = useState(initial?.number || defaultNumber || "");
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const refresh = async () => {
    const { data } = await api.get("/caller-id");
    setState(data || {});
    onChange?.(data || {});
    return data;
  };

  useEffect(() => {
    if (state.number && !state.verified) {
      pollRef.current = setInterval(async () => {
        const d = await refresh().catch(() => null);
        if (d?.verified) { toast.success("Your number is verified — receivers will now see it."); clearInterval(pollRef.current); }
      }, 4000);
      return () => clearInterval(pollRef.current);
    }
  }, [state.number, state.verified]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/caller-id/start", { number });
      setState(data); onChange?.(data);
      if (data.verified) toast.success("This number is already verified.");
      else toast.info("We're calling you now — type the code on your phone's keypad.");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const toggle = async (enabled) => {
    try { const { data } = await api.put("/caller-id/toggle", { enabled }); setState(data); onChange?.(data); }
    catch (e) { toast.error(apiError(e)); }
  };

  const remove = async () => {
    setBusy(true);
    try { await api.delete("/caller-id"); setState({}); onChange?.({}); toast.success("Removed. Calls will show a Zeeptel number again."); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="mt-6 pt-6 border-t border-black/10" data-testid="own-caller-id-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Fingerprint className="h-4 w-4 text-primary" />
            <h2 className="font-heading font-bold">Show my own number when I call</h2>
          </div>
          <p className="text-zinc-500 text-sm">
            Verify your mobile once and the people you call abroad will see <b>your</b> number on their screen and can call
            you back on it. Calls to the US &amp; Canada keep showing &ldquo;ZEEPTEL CALLING&rdquo;.
          </p>
        </div>
        {state.verified && (
          <Switch checked={state.enabled !== false} onCheckedChange={toggle} data-testid="own-caller-id-toggle" aria-label="Show my number" />
        )}
      </div>

      {!state.number && (
        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <input
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            placeholder="+1 651 347 2708"
            data-testid="own-caller-id-input"
            className="flex-1 bg-zinc-50 border border-black/10 rounded-xl px-4 py-3 font-mono focus:ring-2 focus:ring-primary outline-none"
          />
          <button type="button" onClick={start} disabled={busy || number.replace(/\D/g, "").length < 8} data-testid="own-caller-id-start"
            className="h-12 px-5 rounded-xl bg-primary text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PhoneIncoming className="h-4 w-4" />} Verify my number
          </button>
        </div>
      )}

      {state.number && !state.verified && (
        <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="own-caller-id-pending">
          <div className="text-sm font-semibold text-amber-900">We&rsquo;re calling {state.number} now</div>
          <p className="text-xs text-amber-800 mt-1">Answer, then type this code on your phone&rsquo;s keypad:</p>
          <div className="font-mono text-3xl font-bold tracking-[0.3em] text-amber-900 my-2" data-testid="own-caller-id-code">{state.code}</div>
          {state.last_error && <p className="text-xs text-red-600">{state.last_error}</p>}
          <div className="flex gap-3 mt-2 text-xs">
            <button type="button" onClick={start} disabled={busy} className="font-bold text-primary underline" data-testid="own-caller-id-retry">Call me again</button>
            <button type="button" onClick={remove} disabled={busy} className="text-zinc-500 underline" data-testid="own-caller-id-cancel">Cancel</button>
          </div>
        </div>
      )}

      {state.verified && (
        <div className="mt-4 flex items-center justify-between gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="own-caller-id-verified">
          <div className="flex items-center gap-2 text-sm">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            <span className="font-mono font-semibold">{state.number}</span>
            <span className="text-emerald-700">{state.enabled === false ? "verified · off" : "shown to receivers"}</span>
          </div>
          <button type="button" onClick={remove} disabled={busy} data-testid="own-caller-id-remove" aria-label="Remove number"
            className="h-8 w-8 rounded-full text-zinc-400 hover:text-red-600 flex items-center justify-center">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};
